/* eslint-disable camelcase */
import ms from 'ms';
import { featureFlagsService } from './featureFlagsService';
import { gridTypes } from './constants';
import { mobileGridService } from '../services/mobileGridService';
import { isEmpty, omit } from 'lodash';
import * as utils from '../utils';
import { getMobileDevicesFromGrid } from './testimServicesApi';
import type { ProjectData, RunnerOptions } from '../runOptions';
import type { SerializedGrid } from '../services/gridService';
import type { MobileConfig } from 'services/src/commons/mongo/models/testConfig';
import type { ProjectType } from 'services/src/commons/mongo/types/DbProject';
import type { ICapabilities } from 'services/src/commons/mongo/models/customCapabilities';
import type { NativeAppData } from 'src/testRunHandler';

export interface SessionTags {
    testName?: string;
    executionId: string;
    executionName?: string;
    testResultId: string;
    project: string;
    customTag?: string;
    testId?: string;
    branch?: string;
}
export interface AppiumOptionsParams {
    projectData?: ProjectData;
    projectType: ProjectType;
    gridInfo: any;
    testRunConfig?: any;
    nativeAppMetadata: NativeAppData;
    options: RunnerOptions;
    appPath: string | null;
    androidActivityWait: string | null;
    sessionTags: SessionTags;
    shouldRunInEnhancedMode?: boolean;
    customCapabilities?: ICapabilities;
}

interface TDCSelector {
    model?: string;
    os_version?: string;
    device_id?: string;
    manufacturer?: string;
}

interface MobileSessionCaps {
    'appium:options'?: {
        platformName?: 'iOS' | 'Android';
        'tdc:sessionTags'?: Array<{ [tag: string]: string }>;
        'tdc:capture'?: boolean;
        'tdc:controlLock'?: boolean;
        'tdc:selector'?: string;
        'tdc:retryNewSessionFailure'?: boolean; //skip HS-Load-balancer retry
        'tdc:app.id'?: string;
        'appium:autoAcceptAlerts'?: boolean; //ios flag
        'appium:noReset'?: boolean;
        'appium:autoGrantPermissions'?: boolean; //android flag
        'appium:automationName'?: 'XCUITest' | 'UiAutomator2';
        'appium:fullReset'?: boolean;
        'appium:bundleId'?: string;
        'appium:app'?: string;
        'appium:appPackage'?: string;
        'appium:appActivity'?: string;
        'appium:udid'?: string;
    };
}
interface ValidateMobileRunCapabilitiesParams {
    nativeAppMetadata: NativeAppData;
    appPath: string | null;
    gridInfo: SerializedGrid;
}

interface MobileConfigBuilderParams {
    osVersion?: string;
    deviceName?: string;
    mobileConfig?: MobileConfig;
    projectType?: string;
    gridType?: string;
}

interface IsAppium2RequiredParams {
    osVersion: string;
    projectType: string;
    isBrowserStack?: boolean;
    appiumVersion?: string;
}

interface AppiumCapsByProjectTypeTDC extends Omit<AppiumOptionsParams, 'gridInfo' | 'testRunConfig' | 'sessionTags'> {
    sessionCaps: any;
}

interface AppiumCapsByProjectType extends Omit<AppiumCapsByProjectTypeTDC, 'options'> {
    appiumVersion?: string;
    lunchTimeout?: string;
    newCommandTimeout?: number;
    uiautomator2ServerInstallTimeout?: number;
    uiautomator2ServerLaunchTimeout?: number;
    'appium:appiumVersion'?: string;
    'appium:lunchTimeout'?: string;
    'appium:platformVersion'?: string;
    'appium:newCommandTimeout'?: number;
    'appium:uiautomator2ServerInstallTimeout'?: number;
    'appium:uiautomator2ServerLaunchTimeout'?: number;
    'pcloudy:options'?: any;
}

const MOBILE_GRIDS = [
    gridTypes.BROWSERSTACK,
    gridTypes.SAUCELABS,
    gridTypes.TESTIM_TDC,
    gridTypes.TESTIM_TVC,
] as string[];

function validateMobileRunCapabilities({ nativeAppMetadata, appPath, gridInfo }: ValidateMobileRunCapabilitiesParams) {
    if (!nativeAppMetadata && !appPath) {
        throw Error('Application not specified. Please provide the required application for execution and try again.');
    }

    if (!nativeAppMetadata) {
        throw Error('Application not specified. Please provide the required application for execution and try again.');
    }

    if (!nativeAppMetadata.id && !nativeAppMetadata.activity && !nativeAppMetadata.packageName) {
        throw Error(
            'The application appears to be corrupted. Please ensure the application is in a valid state before running the test.',
        );
    }
    if (gridInfo.mode !== 'local' && !MOBILE_GRIDS.includes(gridInfo.type!)) {
        throw Error(
            `unsupported grid was detected please make sure to select supported mobile grid (${gridInfo.type})`,
        );
    }
}

function validateDirectConnectionOptions({
    deviceUdid,
    gridInfo,
    testRunConfig,
}: {
    gridInfo: SerializedGrid;
    testRunConfig: any;
    deviceUdid?: string;
}) {
    const { staticAllocation } = testRunConfig?.mobile || {};
    if (gridInfo.type !== gridTypes.TESTIM_TDC) {
        throw Error(
            'unsupported grid was detected please make sure to select supported mobile grid for direct connection',
        );
    }
    if (!deviceUdid && !staticAllocation?.deviceUdid) {
        throw Error('missing device udid or test run config is not static');
    }
    if (!deviceUdid && staticAllocation?.deviceUdid) {
        const totalDevices = staticAllocation.deviceUdid.split(',').length;
        if (totalDevices !== 1) {
            throw Error('provided static config contains multi devices');
        }
    }
}

function buildTagsTDC(sessionTags: SessionTags) {
    const { testName, executionId, testResultId, customTag } = sessionTags;
    const tags: Array<{ [tag: string]: string }> = [{ executionId }, { testResultId }];

    if (testName) {
        tags.push({ testName });
    }
    if (customTag) {
        tags.push({ customTag });
    }
    return tags;
}

function buildCommonCapsForTDC(
    gridType: string,
    options: RunnerOptions,
    sessionTags: SessionTags,
    customCapabilities?: ICapabilities,
) {
    const { fullReset, setNoReset, skipLoadBalancer } = options;
    const isTDC = gridType === gridTypes.TESTIM_TDC;
    const enableBatchTDC = isTDC && featureFlagsService.flags.enableBatchTDC.isEnabled();
    const increaseTDCRequestTimeout = featureFlagsService.flags.increaseTDCRequestTimeout.isEnabled();
    return {
        [`tdc:options`]: {
            [`tdc:capture.video`]: true,
            ...(featureFlagsService.flags.tdcHsPreventNetworkCapture.isEnabled() && {
                [`tdc:capture.network`]: false,
            }),
            ...(skipLoadBalancer && { [`tdc:controlLock`]: true }),
            [`tdc:retryNewSessionFailure`]: false,
            [`tdc:sessionTags`]: buildTagsTDC(sessionTags),
            [`tdc:enableBatch`]: enableBatchTDC,
            ...(!fullReset && { 'appium:noReset': setNoReset }),
            ...(increaseTDCRequestTimeout && { [`tdc:newCommandTimeout`]: 600, 'appium:newCommandTimeout': 600 }), // 10 minutes
            ...customCapabilities,
        },
    } as MobileSessionCaps;
}

function getApplicationIdentifier(nativeAppMetadata: NativeAppData) {
    return 'id' in nativeAppMetadata ? nativeAppMetadata.id : nativeAppMetadata.packageName;
}

function getGridAppCapability(appIdOnGrid: string) {
    return { 'tdc:app.id': appIdOnGrid };
}

function mergeCapsByProjectTypeHeadSpinOrTDC({
    projectType,
    sessionCaps,
    options,
    nativeAppMetadata,
    appPath,
    androidActivityWait,
}: AppiumCapsByProjectTypeTDC) {
    const { fullReset, setNoReset } = options;
    let appCap: { [key in 'appium:app' | 'tdc:app.id']?: string | null } = {
        'appium:app': appPath,
    };

    const isAppPathUrl = appPath?.startsWith('http');
    const isAppPathHoldsAppIdOnGrid = appPath && !isAppPathUrl;
    if (isAppPathHoldsAppIdOnGrid) {
        appCap = getGridAppCapability(appPath);
    }

    const setFullResetForIos = () => {
        if (fullReset === undefined && appPath) {
            return true;
        }
        if (typeof fullReset === 'string') {
            return fullReset !== 'false';
        }
        return fullReset;
    };

    switch (projectType) {
        case 'ios':
            return {
                ['appium:options']: {
                    ...sessionCaps,
                    platformName: 'iOS',
                    'appium:autoAcceptAlerts': true,
                    'appium:automationName': 'XCUITest',
                    'appium:fullReset': setFullResetForIos(),
                    ...(nativeAppMetadata && { 'appium:bundleId': getApplicationIdentifier(nativeAppMetadata) }),
                    ...(appPath && appCap),
                },
            };
        case 'android':
            return {
                ['appium:options']: {
                    ...sessionCaps,
                    platformName: 'Android',
                    'appium:fullReset': !!fullReset,
                    'appium:autoGrantPermissions': !setNoReset, //noReset need to be false in order to set autoGrantPermissions to true, otherwise it will be ignored
                    'appium:automationName': 'UiAutomator2',
                    ...(androidActivityWait && { 'appium:appWaitActivity': `${androidActivityWait}, *` }),
                    ...(nativeAppMetadata && {
                        'appium:appPackage': getApplicationIdentifier(nativeAppMetadata),
                        'appium:appActivity': nativeAppMetadata.activity ?? '.*',
                    }),
                    ...(appPath && appCap),
                },
            };
        default:
            throw Error(`unsupported mobile project ${projectType}`);
    }
}

function mergeAppiumCapsByProjectType({
    projectType,
    sessionCaps,
    nativeAppMetadata,
    appPath,
    androidActivityWait,
    shouldRunInEnhancedMode,
}: AppiumCapsByProjectType) {
    const { 'appium:options': sessionAppiumOptions, ...restSessionCaps } = sessionCaps;
    switch (projectType) {
        case 'ios':
            return {
                ['appium:options']: {
                    platformName: 'iOS',
                    autoAcceptAlerts: true,
                    automationName: shouldRunInEnhancedMode ? 'WaldoTestim' : 'XCUITest',
                    fullReset: true,
                    ...(nativeAppMetadata && { bundleId: getApplicationIdentifier(nativeAppMetadata) }),
                    ...(appPath && { app: appPath }),
                    ...sessionAppiumOptions,
                },
                // override the default values
                ...restSessionCaps,
            };
        case 'android':
            return {
                ['appium:options']: {
                    platformName: 'Android',
                    autoGrantPermissions: true,
                    automationName: shouldRunInEnhancedMode ? 'WaldoTestim' : 'UiAutomator2',
                    ...(androidActivityWait && { appWaitActivity: `${androidActivityWait}, *` }),
                    ...(nativeAppMetadata && {
                        appPackage: getApplicationIdentifier(nativeAppMetadata),
                        appActivity: nativeAppMetadata.activity ?? '.*',
                    }),
                    ...(appPath && { app: appPath }),
                    ...sessionAppiumOptions,
                },
                ...restSessionCaps,
            };
        default:
            throw Error(`unsupported mobile project ${projectType}`);
    }
}

function buildTdcCaps({
    projectType,
    nativeAppMetadata,
    appPath,
    androidActivityWait,
    sessionTags,
    options,
    gridInfo,
    shouldRunInEnhancedMode,
    customCapabilities,
}: Omit<AppiumOptionsParams, 'testRunConfig'>): MobileSessionCaps {
    const sessionCaps = buildCommonCapsForTDC(gridInfo.type, options, sessionTags, customCapabilities);
    return mergeCapsByProjectTypeHeadSpinOrTDC({
        projectType,
        sessionCaps,
        options,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
        shouldRunInEnhancedMode,
    });
}

function getOsVersion({ osVersion, mobileConfig }: MobileConfigBuilderParams) {
    const { staticAllocation, dynamicAllocation } = mobileConfig!;
    if (osVersion) {
        return osVersion;
    }
    if (staticAllocation?.osVersion) {
        return staticAllocation?.osVersion;
    }
    if (dynamicAllocation?.osVersionRegex) {
        return dynamicAllocation?.osVersionRegex;
    }
    return '.*';
}

function getDeviceName({ deviceName, mobileConfig, projectType, gridType }: MobileConfigBuilderParams) {
    const { staticAllocation, dynamicAllocation } = mobileConfig!;

    if (deviceName) {
        return deviceName;
    }

    if (staticAllocation?.deviceName) {
        return staticAllocation.deviceName;
    }

    if (dynamicAllocation?.deviceNameRegex) {
        return dynamicAllocation.deviceNameRegex;
    }
    //  Browser Stack not accepting .* for iOS devices. This is their way of distinguishing between iPhones and iPads.
    if (projectType === 'ios' && gridType === gridTypes.BROWSERSTACK) {
        return 'iPhone.*';
    }

    return '.*';
}

function getTestimVirtualCloudToken(gridInfo: SerializedGrid, projectType: string) {
    const { androidToken, iosToken } = gridInfo;
    const isIosProject = projectType === 'ios';

    if (!androidToken && !iosToken) {
        throw Error('missing access token for testim virtual cloud');
    }

    if (isIosProject && !iosToken) {
        throw Error('missing ios token for testim virtual cloud');
    }

    if (!isIosProject && !androidToken) {
        throw Error('missing android token for testim virtual cloud');
    }

    return isIosProject ? iosToken : androidToken;
}

function extractCapsFromJson(caps: any, predicate: (key: string) => boolean, replaceString?: string) {
    if (!caps) {
        return {};
    }
    return Object.entries(caps).reduce<any>((acc, [key, value]) => {
        if (predicate(key)) {
            if (!replaceString) {
                acc[key] = value;
                return acc;
            }
            const newKey = key.replace(replaceString, '');
            acc[newKey] = value;
        }
        return acc;
    }, {});
}

function extractBrowserStackOptionsFromJson(caps: any) {
    return extractCapsFromJson(caps, (key) => key.startsWith('browserstack.'), 'browserstack.');
}

function extractAppiumCapsFromBrowserStackJson(caps: any) {
    return extractCapsFromJson(caps, (key) => !key.startsWith('browserstack.'));
}

function setAppiumVersion(appiumVersion?: string) {
    if (!appiumVersion || !isAppium2(appiumVersion)) {
        return '2.0.0';
    }
    return appiumVersion;
}

function isAppium2(platformVersion: string) {
    // https://docs.saucelabs.com/mobile-apps/automated-testing/appium/appium-versions/#real-devices
    // browserstack follow regular appium versioning
    return (
        platformVersion.split('.')[0] === '2' ||
        platformVersion.includes('appium2') ||
        ['latest', 'stable'].includes(platformVersion)
    );
}

function isAppium2Required({ osVersion, projectType, appiumVersion, isBrowserStack = false }: IsAppium2RequiredParams) {
    const android14andAbovePattern = /^(1[4-9](\.\d+){0,2})$|^\^\(\?=1[4-9]((\.\d+){0,2})\.\*\)\.\*$/;
    const androidTenPattern = /^(10(\.\d+){0,2})$|^\^\(\?=10((\.\d+){0,2})\.\*\)\.\*$/;
    const ios17andAbovePattern = /^(1[7-9](\.\d+){0,2})$|^\^\(\?=1[7-9]((\.\d+){0,2})\.\*\)\.\*$/;
    const isBrowserStackRunOnAndroidTen = isBrowserStack && androidTenPattern.test(osVersion);
    const isAppium2RequiredForAndroid =
        projectType === 'android' && (android14andAbovePattern.test(osVersion) || isBrowserStackRunOnAndroidTen);
    const isAppium2RequiredForIOS = projectType === 'ios' && ios17andAbovePattern.test(osVersion);
    const appium2Required = isAppium2RequiredForAndroid || isAppium2RequiredForIOS;

    if (appiumVersion) {
        const isAppium2Requested = isAppium2(appiumVersion);
        if (!isAppium2Requested && appium2Required) {
            // eslint-disable-next-line no-console
            console.warn(`Appium 2 is required for version: ${osVersion}, forcing appium 2 for current session.`);
            return true;
        }
        return isAppium2Requested;
    }
    return appium2Required;
}

function addPrefixToCaps(caps: any, prefix: string) {
    const newCaps = {} as any;
    Object.entries(caps).forEach(([key, value]) => {
        const newKey = key.startsWith(prefix) ? key : `${prefix}${key}`;
        newCaps[newKey] = value;
    });
    return newCaps;
}

async function buildTvcCaps(appiumOptions: AppiumOptionsParams) {
    const {
        projectType,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
        options,
        gridInfo,
        sessionTags,
        testRunConfig,
        shouldRunInEnhancedMode,
        customCapabilities,
    } = appiumOptions;
    const { osVersion, deviceName } = options;
    const mobileConfig = testRunConfig?.mobile || {};
    const { type: gridType } = gridInfo;
    const tokenFromGrid = getTestimVirtualCloudToken(gridInfo, projectType);

    const gridDevices = await getMobileDevicesFromGrid({
        gridId: gridInfo.gridId,
        projectType,
        projectId: options.project,
        companyId: options.company!.companyId,
    });

    const matchingDeviceFromGrid = gridDevices?.find(({ udid }) => mobileConfig.staticAllocation?.deviceUdid === udid);

    // The VMG capabilities object does not support Dynamic allocation.
    // Proposal by Ed to remove Dynamic allocation was made on https://tricentis.atlassian.net/browse/TES-23313?focusedCommentId=882834
    const osVersionCap = matchingDeviceFromGrid?.osVersion ?? getOsVersion({ osVersion, mobileConfig });
    const deviceNameCap =
        matchingDeviceFromGrid?.name ?? getDeviceName({ deviceName, mobileConfig, projectType, gridType });
    const testResultUrl = utils.getTestUrl(
        options.editorUrl!,
        options.project,
        sessionTags.testId!,
        sessionTags.testResultId,
        sessionTags.branch,
    );

    const commonCapabilities: Record<string, any> = {
        'waldo:device': deviceNameCap,
        'waldo:os_version': osVersionCap,
        'waldo:displayName': `${sessionTags.testName}-${sessionTags.executionId}-${sessionTags.testResultId}`,
        'waldo:externalUrl': testResultUrl,
        ...(shouldRunInEnhancedMode && { 'waldo:automationName': 'WaldoTestim' }),
        'waldo:token': tokenFromGrid,
        ...customCapabilities
    };

    if (!commonCapabilities['waldo:options']) {
        commonCapabilities['waldo:options'] = {};
    }

    if (!commonCapabilities['waldo:options'].token) {
        commonCapabilities['waldo:options'].token = tokenFromGrid;
    }

    return mergeAppiumCapsByProjectType({
        projectType,
        sessionCaps: commonCapabilities,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
        shouldRunInEnhancedMode,
    });
}

function buildSauceLabCaps({
    projectType,
    nativeAppMetadata,
    appPath,
    androidActivityWait,
    options,
    gridInfo,
    sessionTags,
    testRunConfig,
    customCapabilities,
}: AppiumOptionsParams) {
    const { osVersion, deviceName } = options;
    const mobileConfig = testRunConfig?.mobile || {};
    const { type: gridType } = gridInfo;
    const osVersionCap = getOsVersion({ osVersion, mobileConfig });
    const deviceNameCap = getDeviceName({ deviceName, mobileConfig, projectType, gridType });

    const jsonAppiumCaps = omit({ ...options.saucelabs }, ['automationName', 'platformName']);
    const jsonOptions = { ...jsonAppiumCaps['sauce:options'] };
    delete jsonAppiumCaps['sauce:options'];

    const customCapabilitiesSauceOptions = { ...customCapabilities?.['sauce:options'] };
    const customCapabilitiesWithoutSauceOptions = omit(customCapabilities, 'sauce:options');
    const appiumVersion = jsonOptions.appiumVersion || customCapabilitiesSauceOptions.appiumVersion;
    const platformVersion = jsonAppiumCaps.platformVersion || customCapabilitiesWithoutSauceOptions.platformVersion || osVersionCap;

    if (
        isAppium2Required({
            osVersion: platformVersion,
            projectType,
            appiumVersion,
        })
    ) {
        const commonOptions = {
            debug: true,
            networkLogs: true,
        };
        const slOptions = {
            user: gridInfo.user,
            key: gridInfo.key,
            name: sessionTags.testResultId,
            build: sessionTags.executionId,
            appiumVersion: setAppiumVersion(appiumVersion ?? 'stable'),
            deviceName: deviceNameCap,
            tags: Object.values(sessionTags),
        };
        const sessionCaps = {
            platformName: projectType === 'android' ? 'Android' : 'iOS',
            browserName: '',
            ['appium:options']: {
                platformVersion: osVersionCap,
                deviceName: deviceNameCap,
                ...jsonAppiumCaps,
                ...customCapabilitiesWithoutSauceOptions,
            },
            'sauce:options': {
                ...commonOptions,
                ...slOptions,
                ...omit(jsonOptions, 'appiumVersion'),
                ...omit(customCapabilitiesSauceOptions, 'appiumVersion'),
            },
        };
        return mergeAppiumCapsByProjectType({
            projectType,
            sessionCaps,
            nativeAppMetadata,
            appPath,
            androidActivityWait,
        });
    }

    const commonCapabilities = {
        'sauce:options': {
            'saucelabs.user': gridInfo.user,
            'saucelabs.key': gridInfo.key,
            tags: Object.values(sessionTags),
            name: sessionTags.testResultId,
            build: sessionTags.executionId,
        },
        platformName: projectType === 'android' ? 'Android' : 'iOS',
        browserName: '',
        'appium:platformVersion': osVersionCap,
        'appium:deviceName': deviceNameCap,
        'appium:device': deviceNameCap,
        ...jsonAppiumCaps,
        ...jsonOptions,
        ...customCapabilitiesWithoutSauceOptions,
        ...customCapabilitiesSauceOptions,
    };

    return mergeAppiumCapsByProjectType({
        projectType,
        sessionCaps: commonCapabilities,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
    });
}

function buildBrowserStackCaps({
    projectData,
    projectType,
    nativeAppMetadata,
    appPath,
    androidActivityWait,
    options,
    gridInfo,
    sessionTags,
    testRunConfig,
    customCapabilities,
}: AppiumOptionsParams) {
    const { osVersion, deviceName } = options;
    const mobileConfig = testRunConfig?.mobile || {};
    const { type: gridType } = gridInfo;
    const osVersionCap = getOsVersion({ osVersion, mobileConfig });
    const deviceNameCap = getDeviceName({ deviceName, mobileConfig, projectType, gridType });
    const jsonOptions = extractBrowserStackOptionsFromJson(options.browserstack);
    const jsonAppiumCaps = omit(extractAppiumCapsFromBrowserStackJson(options.browserstack), [
        'automationName',
        'platformName',
        'project',
        'build',
        'name',
    ]);

    const customCapabilitiesBrowserStackOptions = { ...customCapabilities?.['bstack:options'] };
    const customCapabilitiesWithoutBrowserStackOptions = omit(customCapabilities, 'bstack:options');
    const appiumVersion = jsonOptions.appiumVersion || customCapabilitiesBrowserStackOptions.appiumVersion;
    const platformVersion = jsonAppiumCaps.platformVersion || customCapabilitiesWithoutBrowserStackOptions.platformVersion || osVersionCap;
    const shouldForceAppium2 = featureFlagsService.flags.enableAppium2BrowserStack.isEnabled();

    const baseOptions = {
        buildName: `${sessionTags.executionName} - ${sessionTags.executionId}`,
        projectName: `${projectData!.name} - ${projectData!.projectId}`,
        sessionName: `${sessionTags.testName} - ${sessionTags.testId}`,
    };

    if (
        shouldForceAppium2 ||
        isAppium2Required({
            osVersion: platformVersion,
            projectType,
            isBrowserStack: true,
            appiumVersion,
        })
    ) {
        const browserstackOptions = {
            ...baseOptions,
            userName: gridInfo.user,
            accessKey: gridInfo.key,
            appiumVersion: shouldForceAppium2 ? '2.0.1' : setAppiumVersion(appiumVersion),
            debug: true,
            networkLogs: true,
        };

        const sessionCaps = {
            ['appium:options']: {
                platformVersion: osVersionCap,
                deviceName: deviceNameCap,
                ...jsonAppiumCaps,
                ...customCapabilitiesWithoutBrowserStackOptions,
            },
            'bstack:options': {
                ...browserstackOptions,
                ...omit(jsonOptions, 'appiumVersion'),
                ...omit(customCapabilitiesBrowserStackOptions, 'appiumVersion'),
            },
        };

        return mergeAppiumCapsByProjectType({
            projectType,
            sessionCaps,
            nativeAppMetadata,
            appPath,
            androidActivityWait,
        });
    }

    const commonCapabilities = {
        ...baseOptions,
        // required because we force clients to follow W3C format for appium capabilities.
        os_version: jsonAppiumCaps.platformVersion || osVersionCap,
        device: jsonAppiumCaps.deviceName || deviceNameCap,
        realDevice: true,
        buildTags: Object.values(sessionTags).join(','),
        'browserstack.user': gridInfo.user,
        'browserstack.key': gridInfo.key,
        'browserstack.debug': true,
        'browserstack.networkLogs': true,
        ...jsonAppiumCaps,
        ...addPrefixToCaps(jsonOptions, 'browserstack.'),
        ...customCapabilitiesWithoutBrowserStackOptions,
        ...addPrefixToCaps(customCapabilitiesBrowserStackOptions, 'browserstack.'),
    };

    return mergeAppiumCapsByProjectType({
        projectType,
        sessionCaps: commonCapabilities,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
    });
}

async function buildAppiumSessionCapabilities(appiumOptions: AppiumOptionsParams) {
    if (gridTypes.TESTIM_TDC === appiumOptions.gridInfo.type) {
        return buildTdcCaps(appiumOptions);
    }
    if (gridTypes.TESTIM_TVC === appiumOptions.gridInfo.type) {
        return await buildTvcCaps(appiumOptions);
    }
    if (gridTypes.SAUCELABS === appiumOptions.gridInfo.type) {
        return buildSauceLabCaps(appiumOptions);
    }
    if (gridTypes.BROWSERSTACK === appiumOptions.gridInfo.type) {
        return buildBrowserStackCaps(appiumOptions);
    }
    throw new Error(`unsupported grid type ${appiumOptions.gridInfo.type}`);
}

function generateTDCgridSelectors(options: RunnerOptions, mobileConfig?: MobileConfig) {
    const { staticAllocation, dynamicAllocation, type } = mobileConfig || {};
    const { deviceName, osVersion, deviceUdid } = options;
    const tdcSelector = {} as TDCSelector;

    if (type === 'dynamic') {
        if (!deviceName && dynamicAllocation?.selector?.manufacturer) {
            tdcSelector.manufacturer = dynamicAllocation.selector.manufacturer;
        }
        if (!osVersion && dynamicAllocation?.selector?.osVersion) {
            tdcSelector.os_version = dynamicAllocation.selector.osVersion;
        }
    }
    if (type === 'static' && !deviceUdid && staticAllocation?.deviceUdid) {
        tdcSelector.device_id = staticAllocation.deviceUdid;
    }

    if (deviceName) {
        tdcSelector.manufacturer = deviceName;
    }
    if (osVersion) {
        tdcSelector.os_version = osVersion;
    }

    if (deviceUdid) {
        tdcSelector.device_id = deviceUdid;
        delete tdcSelector.os_version;
        delete tdcSelector.manufacturer;
    }
    return tdcSelector;
}

export async function buildAppiumLoadBalancerOptions(appiumOptions: AppiumOptionsParams) {
    const { gridInfo, nativeAppMetadata, options, appPath, testRunConfig } = appiumOptions;
    const { selectedGrid } = testRunConfig?.mobile || {};
    if (testRunConfig?.mobile && selectedGrid?.type !== gridInfo.type) {
        throw Error(`selected grid type ${selectedGrid.type} is not equal to grid type ${gridInfo.type}`);
    }
    validateMobileRunCapabilities({ nativeAppMetadata, appPath, gridInfo });

    const connection = mobileGridService.getGridConnection(gridInfo);
    const sessionCaps = await buildAppiumSessionCapabilities(appiumOptions);

    //only for tdc grid
    if (gridInfo.type === gridTypes.TESTIM_TDC) {
        const tdcSelector = generateTDCgridSelectors(options, testRunConfig?.mobile);
        if (!isEmpty(tdcSelector)) {
            const selector = Object.entries(tdcSelector)
                .map(([key, value]) =>
                    value
                        .split(',')
                        .map((val: string) => (val === '.*' || val === '=' ? '' : `${key}:${val}`))
                        .join(','),
                )
                .join(' ')
                .trim();
            sessionCaps['tdc:selector' as keyof typeof sessionCaps] = selector;
        }
    }
    const capabilities = gridInfo.type === gridTypes.TESTIM_TVC ? { alwaysMatch: sessionCaps } : sessionCaps;

    const increaseTDCRequestTimeout = featureFlagsService.flags.increaseTDCRequestTimeout.isEnabled();
    const DISABLE_RETRIES = 0;
    const DEFAULT_REQUEST_TIMEOUT = ms('2m');
    const TWELVE_MINUTES = ms('12m');
    const connectionRetryCount = utils.sanitizeNumberValue(
        featureFlagsService.flags.appiumConnectionRetryCount.getValue(),
        DISABLE_RETRIES,
    );
    let connectionRetryTimeout = utils.sanitizeNumberValue(
        featureFlagsService.flags.appiumConnectionRequestTimeout.getValue(),
        DEFAULT_REQUEST_TIMEOUT,
    );
    if (increaseTDCRequestTimeout && connectionRetryTimeout < TWELVE_MINUTES) {
        connectionRetryTimeout = TWELVE_MINUTES;
    }

    return {
        ...connection,
        desiredCapabilities: sessionCaps,
        connectionRetryTimeout,
        capabilities,
        connectionRetryCount,
        logLevel: options.appiumLogLevel,
    };
}

export async function buildAppiumDirectConnectionOptions(appiumOptions: AppiumOptionsParams) {
    const {
        projectType,
        gridInfo,
        nativeAppMetadata,
        options,
        appPath,
        androidActivityWait,
        sessionTags,
        testRunConfig,
    } = appiumOptions;
    const { deviceUdid } = options;
    validateMobileRunCapabilities({ nativeAppMetadata, appPath, gridInfo });
    validateDirectConnectionOptions({ deviceUdid, gridInfo, testRunConfig });
    //cli flag overrides given config
    const deviceId = deviceUdid || testRunConfig.mobile?.staticAllocation?.deviceUdid;
    const connection = await mobileGridService.getGridDirectConnection(gridInfo, deviceId);
    const params = {
        projectType,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
        options,
        sessionTags,
        testRunConfig,
        gridInfo,
    };
    const sessionCaps = buildTdcCaps(params);

    if (deviceId) {
        sessionCaps['appium:options'] ||= {};
        sessionCaps['appium:options']['appium:udid'] = deviceId;
    }

    return {
        ...connection,
        desiredCapabilities: sessionCaps,
        capabilities: sessionCaps,
        connectionRetryCount: 0,
        logLevel: options.appiumLogLevel,
    };
}

export function buildAppiumLocalTmaCapabilities(appiumOptions: AppiumOptionsParams) {
    const { projectType, nativeAppMetadata, appPath, androidActivityWait, customCapabilities } = appiumOptions;

    const sessionCaps = {
        ...customCapabilities,
        // Set to true to keep the app's process alive during testing (appium inspector)
        dontStopAppOnReset: true,
        // prevent app from being uninstalled on session start for ios runs
        fullReset: false,
    };

    const mergedCaps = mergeAppiumCapsByProjectType({
        projectType,
        sessionCaps,
        nativeAppMetadata,
        appPath,
        androidActivityWait,
    });

    if (projectType === 'ios') {
        // iOS cause the app to be installed on the device, so we need to delete the app before creating the session
        delete mergedCaps['appium:options'].app;
    }

    return mergedCaps;
}

export function removeAccessDataFromCapabilities(capabilities: any) {
    const caps = { ...capabilities };
    delete caps.path;
    delete caps.user;
    delete caps.key;
    delete caps.desiredCapabilities;
    delete caps.capabilities['saucelabs.user'];
    delete caps.capabilities['saucelabs.key'];
    delete caps.capabilities['browserstack.user'];
    delete caps.capabilities['browserstack.key'];
    delete caps.capabilities['sauce:options']?.user;
    delete caps.capabilities['sauce:options']?.key;
    delete caps.capabilities.key;
    delete caps.capabilities.user;
    return caps;
}
